import React from 'react';
import AppHeader from '../../../app/HeaderComponent/AppHeader';
import { Banner } from '../../../app/Banner';
import BadgeModal from '../../../shared/BadgeModal';
import { useGetAllBadges } from '../../../../queries';
import { useParams } from '../../../../hooks';
import UpgradeBanner from '../../../app/HeaderComponent/UpgradeBanner';
import { useLocation } from 'react-router-dom';
import withCopyPasteProtection from '../../withCopyPasteProtection';
import SideNav from '../../../app/SideNav';
import {
  ASK_ME_QUESTIONS_URL,
  LEARN_A_TOPIC_URL,
  PRACTICE_QUESTIONS_URL
} from '../../../../constants';

const SIDE_NAV_PREFIXES = [PRACTICE_QUESTIONS_URL, ASK_ME_QUESTIONS_URL, LEARN_A_TOPIC_URL];

const shouldShowSideNav = (pathname, welcomeMode, testMode) => {
  if (welcomeMode || testMode) return false;
  if (pathname === '/' || pathname === '/activity' || pathname === '/performance') return true;
  // Topic selection only — hide on chat threads like /practice-questions/:id
  return SIDE_NAV_PREFIXES.includes(pathname);
};

const isChatThreadPage = (pathname) =>
  SIDE_NAV_PREFIXES.some((path) => pathname.startsWith(`${path}/`));

const AppLayout = ({ children, headerProps, bannerProps, welcomeMode = false, testMode = false }) => {
  const { data: userBadges, isLoading } = useGetAllBadges();
  const [isBadgeOpen, setIsBadgeOpen] = React.useState(false);
  const { isLoggedIn } = useParams();
  const location = useLocation();
  const isStartPage = location.pathname.includes('/start');
  const isAssignmentPage = location.pathname.includes('/assignment');
  const showSideNav = shouldShowSideNav(location.pathname, welcomeMode, testMode);
  const isHomePage = location.pathname === '/';
  const isActivityPage = location.pathname === '/activity';
  const isPerformancePage = location.pathname === '/performance';
  const isTopicSelectionPage = SIDE_NAV_PREFIXES.includes(location.pathname);
  const isChatPage = isChatThreadPage(location.pathname);
  const isFullHeightPage = isHomePage || isActivityPage || isPerformancePage || isTopicSelectionPage || isChatPage;

  const onBadgeOpen = React.useCallback(() => setIsBadgeOpen(true), []);
  const onBadgeClose = React.useCallback(() => setIsBadgeOpen(false), []);

  React.useEffect(() => {
    if (userBadges && isLoggedIn && !isStartPage && !isAssignmentPage) {
      const userSelectedBadges = userBadges.filter((badge) => badge.isSelected);

      if (userSelectedBadges.length === 0) {
        onBadgeOpen();
      }
    }
  }, [userBadges, onBadgeOpen, isLoggedIn, location.pathname, isStartPage, isAssignmentPage]);

  const contentClassName = welcomeMode
    ? 'flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white'
    : testMode
      ? 'flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white pt-[52px]'
      : [
          'flex min-h-0 w-full flex-1 flex-col bg-white',
          isTopicSelectionPage || isChatPage || isPerformancePage ? '' : 'container-fluid',
          isFullHeightPage || isChatPage ? 'overflow-hidden' : 'overflow-auto'
        ]
          .filter(Boolean)
          .join(' ');

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-white">
      <AppHeader {...headerProps} onBadgeOpen={onBadgeOpen} welcomeMode={welcomeMode} testMode={testMode} />
      {!welcomeMode && !testMode && <UpgradeBanner />}
      <Banner {...bannerProps} />
      <div className="flex min-h-0 w-full flex-1 overflow-hidden">
        {showSideNav ? (
          <aside className="hidden w-20 shrink-0 self-stretch md:flex">
            <SideNav />
          </aside>
        ) : null}
        <div className={contentClassName}>{children}</div>
      </div>
      {showSideNav ? (
        <div className="z-40 shrink-0 border-t border-[#E5E7EB] bg-white pb-[env(safe-area-inset-bottom,0px)] md:hidden">
          <div className="h-16">
            <SideNav />
          </div>
        </div>
      ) : null}
      {!isLoading && isBadgeOpen && (
        <BadgeModal isBadgeOpen={isBadgeOpen} onBadgeClose={onBadgeClose} />
      )}
    </div>
  );
};

const AppCommonLayout = withCopyPasteProtection(AppLayout, {
  enableStrictMode: (props) => props?.enableStrictMode ?? false
});

export default AppCommonLayout;
